import { rem } from 'polished'
import clsx from 'clsx'
import React, { ComponentProps } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import Clickable from '../Common/Clickable'
import BodyText from '../../../LuxKit/Typography/BodyText'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import Badge from 'LuxKit/Badge'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'

const Item = styled(Clickable)`
  display: flex;
  align-items: start;
  gap: ${rem(12)};
  width: 100%;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: background-color 0.2s, color 0.2s, box-shadow 0.2s;
  border-radius: ${props => props.theme.borderRadius.S};
  box-shadow: none;
  color: ${props => props.theme.palette.neutral.default.one};
  padding-block: ${rem(8)};

  &:not(.indent) {
    padding-inline: ${rem(20)};

    ${mediaQueryUp.tablet} {
      padding-inline: ${rem(24)};
    }
  }

  &.indent {
    padding-inline: ${rem(48)} ${rem(20)};

    ${mediaQueryUp.tablet} {
      padding-inline: ${rem(52)} ${rem(24)};
    }
  }

  &.selected {
    background-color: ${props => props.theme.palette.highlight.secondary.lightBackground};
  }

  &:disabled {
    color: ${props => props.theme.palette.neutral.default.four};
  }

  &:not(:disabled, .nonInteractable) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.default.seven};
      }
    }

    &:focus {
      ${interactiveFocusStyle}
    }
  }
`

const Content = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
`

interface Props extends
  Omit<ComponentProps<typeof Clickable>, 'title' | 'children'> {
  /** An icon element that will go at the start (often the 'left' side) of the AccountMenuListItem item. */
  startIcon?: React.ReactNode
  /**
   * An element that will go at the end i.e. right side of the AccountMenuListItem item.
   * i.e. badge icon or supporting copy like '2 upcoming'
   */
  title: React.ReactNode
  subtitle?: React.ReactNode
  className?: string
  /**
   * Children will be placed in the 'slot' specified in Figma.
   */
  children?: React.ReactNode
  /**
   * Disables the visual interactability feedbacks such as hover and focus.
   *
   * Turns the component into a `div`.
   */
  nonInteractable?: boolean
  endTitle?: React.ReactNode
  endIcon?: React.ReactNode
  indent?: boolean

  titleFormat?: ComponentProps<typeof BodyText>['format']
  titleLineClamp?: ComponentProps<typeof BodyText>['lineClamp']
  titleColour?: ComponentProps<typeof BodyText>['colour']

  subtitleFormat?: ComponentProps<typeof BodyText>['format']
  subtitleLineClamp?: ComponentProps<typeof BodyText>['lineClamp']

  newBadge?: boolean
}

function NavBarListItem(props: Props) {
  const {
    startIcon,
    className,
    children,
    nonInteractable,
    disabled,
    endTitle,
    endIcon,
    indent,
    title,
    titleFormat,
    titleLineClamp,
    titleColour,
    subtitle,
    subtitleFormat,
    subtitleLineClamp,
    newBadge,
    ...buttonProps
  } = props

  const intl = useIntl()

  const newBadgeText = intl.formatMessage({
    id: 'vZaJcAcVXq',
    defaultMessage: 'New',
    description: 'New badge label for navigation list items',
  })

  return (
    <Item
      {...buttonProps}
      disabled={disabled}
      as={nonInteractable ? ('div' as any) : undefined}
      className={clsx(
        className,
        {
          nonInteractable,
          indent,
        },
      )}
    >
      {!!startIcon && <div>
        {startIcon}
      </div>}

      <Content>
        <Group direction="vertical" noDisplayWhenEmpty>
          {!!title && <BodyText
            variant="medium"
            format={titleFormat}
            lineClamp={titleLineClamp}
            colour={titleColour}
          >
            {title}
          </BodyText>}
          {!!subtitle && <BodyText
            variant="small"
            colour="neutral-three"
            format={subtitleFormat}
            lineClamp={subtitleLineClamp}
          >
            {subtitle}
          </BodyText>}
        </Group>

        {children}

        <div>
          {!!endTitle && <BodyText
            variant="medium"
            colour="neutral-two"
          >
            {endTitle}
          </BodyText>}
          {endIcon}

          {newBadge && <Badge mode="critical" kind="string">{newBadgeText}</Badge>}
        </div>
      </Content>
    </Item>
  )
}

export default NavBarListItem
